import * as React from 'react';
import { DatasheetHolderSelectionBox } from '../../components/datasheet/components/datasheet-holder/datasheet-holder.types';
import { DatasheetElement, DatasheetElementId } from '@modules/datasheets/types/datasheet-element';
import { IDatasheetComponentDto } from '@common/dto/datasheet/datasheet-component';

type Props = {
  datasheetElements: Record<DatasheetElementId, DatasheetElement>;
  selectedDatasheetElementsId: Array<DatasheetElementId>;
  onSelectedDatasheetElementsReset: () => void;
  onSelectedDatasheetElementsIdChange: (elements: Array<DatasheetElementId> | ((els: Array<DatasheetElementId>) => Array<DatasheetElementId>)) => void;
};

export const useDatasheetElementsSelectionController = (props: Props) => {
  const { selectedDatasheetElementsId, onSelectedDatasheetElementsIdChange, datasheetElements, onSelectedDatasheetElementsReset } = props;

  const findSelectedElements = React.useCallback((selectionBox: DatasheetHolderSelectionBox, elements: Record<DatasheetElementId, DatasheetElement>): Array<DatasheetElementId> => {
    const selectedSquare = {
      x1: selectionBox.x,
      x2: selectionBox.x + selectionBox.width,
      y1: selectionBox.y,
      y2: selectionBox.y + selectionBox.height,
    };

    const isElementInSelection = (element: DatasheetElement) => {
      const elementSquare = {
        x1: element.coordinates.x,
        x2: element.coordinates.x + element.width,
        y1: element.coordinates.y,
        y2: element.coordinates.y + element.height,
      };

      const isIntersecting = !(
        elementSquare.x2 < selectedSquare.x1 || 
        elementSquare.x1 > selectedSquare.x2 || 
        elementSquare.y2 < selectedSquare.y1 ||
        elementSquare.y1 > selectedSquare.y2
      );

      return isIntersecting;
    }

    const result = Object.keys(elements).reduce<Array<DatasheetElementId>>((acc, elementId) => {
      const element = elements[elementId];

      if (element && isElementInSelection(element)) {
        acc.push(element.id);
      }

      return acc;
    }, []);

    return result;
  }, []);

  const handleSelectionReset = React.useCallback(() => {
    onSelectedDatasheetElementsReset();
  }, [onSelectedDatasheetElementsReset]);

  const isDatasheetElementSelected = React.useCallback(<T extends IDatasheetComponentDto>(datasheetElement: DatasheetElement<T>) => {
    return selectedDatasheetElementsId.includes(datasheetElement.id);
  }, [selectedDatasheetElementsId]);

  const handleDatasheetElementSelect = React.useCallback(<T extends IDatasheetComponentDto>(datasheetElement: DatasheetElement<T>, e: React.MouseEvent<HTMLDivElement>) => {
    const callToPreventSelectionReset = () => {
      e.stopPropagation();
    };
    
    callToPreventSelectionReset();

    onSelectedDatasheetElementsIdChange((selectedElementsId) => {
      if (e.ctrlKey) {
        if (isDatasheetElementSelected(datasheetElement)) {
          return selectedElementsId;
        }
        
        return selectedElementsId.concat(datasheetElement.id);
      }

      return [datasheetElement.id];
    });
  }, [isDatasheetElementSelected]);

  const selectedDatasheetElementsCount = React.useMemo(() => {
    return selectedDatasheetElementsId.length;
  }, [selectedDatasheetElementsId]);

  const handleSelectionEnd = React.useCallback((selection: DatasheetHolderSelectionBox) => {
    const selectedElements = findSelectedElements(selection, datasheetElements);

    onSelectedDatasheetElementsIdChange(selectedElements);
  }, [datasheetElements]);

  return {
    handleDatasheetElementSelect,
    handleSelectionReset,
    isDatasheetElementSelected,
    selectedDatasheetElementsCount,
    handleSelectionEnd,
  };
};